<template>
  <nav v-if="crumbs.length > 1" class="app-breadcrumbs" :aria-label="t('navigation.breadcrumbs')">
    <button class="app-breadcrumbs__back" :aria-label="t('navigation.backTo', { page: label(parent) })" type="button" @click="returnToParent(router, parent.to)">
      <v-icon icon="mdi-arrow-left" size="17" aria-hidden="true" />
      <span class="app-breadcrumbs__back-full">{{ t('navigation.backTo', { page: label(parent) }) }}</span>
      <span class="app-breadcrumbs__back-short" aria-hidden="true">{{ label(parent) }}</span>
    </button>
    <ol>
      <li v-for="(crumb, index) in crumbs" :key="`${crumb.key}-${index}`">
        <v-icon v-if="index" icon="mdi-chevron-right" size="15" aria-hidden="true" />
        <span v-if="index === crumbs.length - 1 && crumb.key !== 'event'" aria-current="page" :title="label(crumb)">{{ label(crumb) }}</span>
        <router-link v-else :to="crumb.to" :title="label(crumb)">{{ label(crumb) }}</router-link>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed, onScopeDispose, ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useClubStore } from '@/stores/club'
import { breadcrumbsFor, returnToParent, type Breadcrumb } from './navigation'

const route = useRoute(), router = useRouter(), { t, te } = useI18n(), clubs = useClubStore()
// Observe the shared V2 query cache; page refetches/realtime invalidation refresh
// the title without another API request or a separate stale copy of event data.
const queryClient = useQueryClient(), auth = useAuthStore(), cacheRevision = ref(0)
const unsubscribe = queryClient.getQueryCache().subscribe(event => {
  if (['athleteJournal', 'tryoutContext', 'tryoutHome'].includes(String(event.query.queryKey[0]))) cacheRevision.value++
})
onScopeDispose(unsubscribe)
const eventTitle = computed(() => {
  void cacheRevision.value
  const club = String(route.params.clubId || ''), season = String(route.params.seasonId || ''), eventId = String(route.params.tryoutId || '')
  if (!club || !season || !eventId) return ''
  const keys = [
    ['athleteJournal', club, season, eventId, auth.user?.id || ''],
    ['tryoutContext', club, season, eventId],
    ['tryoutHome', season, eventId, club, auth.user?.details?.id || auth.user?.id || ''],
  ]
  return keys.map(queryKey => queryClient.getQueryCache().find({queryKey, exact:true}))
    .filter(query => query?.state.status === 'success')
    .sort((a,b) => (b?.state.dataUpdatedAt || 0) - (a?.state.dataUpdatedAt || 0))
    .map(query => (query?.state.data as {event?: {id?: string; title?: string}} | undefined)?.event)
    .find(event => event?.id === eventId && typeof event.title === 'string' && event.title.trim())?.title || ''
})
const crumbs = computed(() => breadcrumbsFor(route, router.options.history.state.settingsReturnTo))
// A settings tab is part of its settings page; returning leaves the page rather
// than resetting its selected tab.
const parent = computed(() => crumbs.value.filter(crumb => !crumb.tab).slice(-2)[0] || crumbs.value[0]!)
function label(crumb: Breadcrumb) {
  if (crumb.key === 'event') return eventTitle.value || t('navigation.event')
  if (crumb.key === 'club') return clubs.data[String(route.params.clubId)]?.name || t('navigation.club')
  if (crumb.tab) return te(crumb.tab) ? t(crumb.tab, { participant: t('navigation.participants') }) : t('navigation.section')
  return t(`navigation.${crumb.key}`)
}
</script>

<style scoped>
/* The header scrolls with the layout; its reserved height is not a sticky offset. */
.app-breadcrumbs { position: sticky; top: 0; z-index: 4; display: flex; align-items: center; gap: 16px; padding: 7px 18px; min-height: 38px; background: #f6f8fb; border-bottom: 1px solid #dce3eb; color: #42576a; font-size: .78rem; }
.app-breadcrumbs__back { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; font-weight: 600; color: #173a53; border-right: 1px solid #dce3eb; padding-right: 16px; }
.app-breadcrumbs ol { display: flex; align-items: center; gap: 6px; list-style: none; margin: 0; padding: 0; min-width: 0; flex-wrap: wrap; }
.app-breadcrumbs li { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
.app-breadcrumbs a { color: inherit; text-decoration: none; }
.app-breadcrumbs a:hover { text-decoration: underline; }
.app-breadcrumbs li:last-child { font-weight: 600; }
.app-breadcrumbs button:focus-visible, .app-breadcrumbs a:focus-visible { outline: 2px solid #006fa3; outline-offset: 3px; border-radius: 2px; }
.app-breadcrumbs__back-short { display: none; }
@media (max-width: 600px) {
  .app-breadcrumbs { padding: 0 12px; gap: 6px; flex-wrap: nowrap; min-height: 44px; }
  .app-breadcrumbs__back { border-right: 0; padding-right: 0; min-height: 44px; min-width: 44px; max-width: 55%; }
  .app-breadcrumbs__back-full { display: none; }
  .app-breadcrumbs__back-short { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .app-breadcrumbs ol { flex: 1; flex-wrap: nowrap; overflow: hidden; }
  .app-breadcrumbs li:not(:last-child) { display: none; }
  .app-breadcrumbs li:last-child { overflow: hidden; }
  .app-breadcrumbs li:last-child > a, .app-breadcrumbs [aria-current] { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}
</style>
